import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
  listScenarios, deleteScenario, duplicateScenario, linkAdvisorByCode, previewAdvisorByCode, listMyAdvisors, revokeAdvisorAccess,
  ADVISOR_CONSENT_TEXT, type ScenarioRow, type MyAdvisorLink, type AdvisorPreview,
} from "@/lib/scenarios";
import { normalizeAdvisorCode } from "@/lib/advisorCode";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import type { ScenarioStatus } from "@/lib/scenarios";
import { ActualPaymentTracking } from "@/components/ActualPaymentTracking";
import { Checkbox } from "@/components/ui/checkbox";

export default function Scenarios() {
  const { user, signOut } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState<ScenarioRow[] | null>(null);
  const [code, setCode] = useState("");
  const [linking, setLinking] = useState(false);
  const [openTracker, setOpenTracker] = useState<string | null>(null);
  const [consented, setConsented] = useState(false);
  const [advisors, setAdvisors] = useState<MyAdvisorLink[]>([]);
  const [pending, setPending] = useState<AdvisorPreview | null>(null);

  useEffect(() => {
    listScenarios().then(setRows).catch((e) => toast.error(e.message));
  }, []);

  useEffect(() => {
    if (!user) return;
    listMyAdvisors(user.id).then(setAdvisors).catch(() => setAdvisors([]));
  }, [user]);

  const onDelete = async (id: string) => {
    if (!confirm("Delete this scenario? This cannot be undone.")) return;
    try {
      await deleteScenario(id);
      setRows((r) => (r ? r.filter((s) => s.id !== id) : r));
      toast.success("Scenario deleted");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to delete");
    }
  };

  /**
   * Step 1 — resolve the code to a person. Nothing is granted here.
   *
   * Codes are short and human-dictated, so a single wrong character can land
   * on another live advisor. The client sees who they are about to share with
   * before any access exists.
   */
  const onLinkAdvisor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    if (!consented) { toast.error("Please confirm the consent statement first"); return; }
    setLinking(true);
    try {
      const found = await previewAdvisorByCode(code);
      if (found) setPending(found);
      else toast.error("Code not recognized");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to look up that code");
    } finally {
      setLinking(false);
    }
  };

  const advisorLabel = (a: AdvisorPreview) => a.display_name || "this advisor";

  /** Step 2 — the client has seen the name and confirmed. Grant access. */
  const onConfirmLink = async () => {
    if (!pending) return;
    setLinking(true);
    try {
      const ok = await linkAdvisorByCode(code);
      if (ok) {
        toast.success(`Connected to ${advisorLabel(pending)} with your consent`);
        setCode("");
        setConsented(false);
        setPending(null);
        if (user) setAdvisors(await listMyAdvisors(user.id));
      } else {
        toast.error("Code not recognized");
        setPending(null);
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to link");
    } finally {
      setLinking(false);
    }
  };

  const onRevoke = async (advisorId: string) => {
    if (!confirm("Revoke this advisor's access? They will immediately lose visibility of your scenarios.")) return;
    try {
      await revokeAdvisorAccess(advisorId);
      if (user) setAdvisors(await listMyAdvisors(user.id));
      toast.success("Advisor access revoked");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to revoke");
    }
  };

  const onCopy = async (s: ScenarioRow) => {
    if (!user) return;
    try {
      const copy = await duplicateScenario(user.id, s, rows.map((r) => r.name));
      setRows((prev) => [copy, ...prev]);
      toast.success(`Copied as "${copy.name}"`, {
        description: "The copy is a Draft with its own Loan ID.",
        action: { label: "Open copy", onClick: () => nav(`/?scenario=${copy.id}`) },
      });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to copy scenario");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>My Scenarios — Family Loan Planner</title>
        <meta name="description" content="View, open, and manage your saved Family Loan Planner scenarios." />
      </Helmet>
      <header className="border-b bg-primary text-primary-foreground">
        <div className="container mx-auto h-16 px-4 flex items-center justify-between">
          <Link to="/" className="font-semibold">Family Loan Planner™</Link>
          <div className="flex items-center gap-3 text-sm">
            <span className="opacity-80 hidden sm:inline">{user?.email}</span>
            <Button variant="secondary" size="sm" onClick={async () => { await signOut(); nav("/"); }}>
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-6 max-w-4xl">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">My Scenarios</h1>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => nav("/timeline")}>View Timeline</Button>
            <Button onClick={() => nav("/")}>+ New scenario</Button>
          </div>
        </div>

        <Card className="p-4 space-y-4">
          <form onSubmit={onLinkAdvisor} className="space-y-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Advisor code (optional)</label>
              <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter the code your advisor gave you" />
            </div>
            <label className="flex items-start gap-2 text-xs text-muted-foreground">
              <Checkbox checked={consented} onCheckedChange={(v) => setConsented(v === true)} className="mt-0.5" />
              <span>{ADVISOR_CONSENT_TEXT}</span>
            </label>
            {pending ? (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-2">
                <p className="text-sm">
                  Code <span className="font-mono font-medium">{normalizeAdvisorCode(code)}</span> belongs to{" "}
                  <span className="font-semibold">{pending.display_name || "an advisor with no name on file"}</span>.
                </p>
                <p className="text-xs text-muted-foreground">
                  Confirm only if that is the advisor you meant. They will be able to read your saved scenarios.
                </p>
                <div className="flex gap-2">
                  <Button type="button" size="sm" onClick={onConfirmLink} disabled={linking}>
                    Yes, connect {pending.display_name || "this advisor"}
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setPending(null)} disabled={linking}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button type="submit" variant="outline" disabled={linking || !code.trim() || !consented}>
                Connect advisor
              </Button>
            )}
          </form>

          {advisors.length > 0 && (
            <div className="border-t pt-3 space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Connected advisors</p>
              {advisors.map((a) => (
                <div key={a.advisor_user_id} className="flex items-center justify-between gap-3 text-xs">
                  <div className="min-w-0">
                    <div className="font-mono truncate">{a.linked_via_code || a.advisor_user_id.slice(0, 8)}</div>
                    <div className="text-muted-foreground">
                      {a.revoked_at
                        ? `Revoked ${new Date(a.revoked_at).toLocaleDateString()}`
                        : `Consent ${a.consent_version} granted ${new Date(a.granted_at).toLocaleDateString()}`}
                    </div>
                  </div>
                  {a.revoked_at ? (
                    <Badge variant="outline">No access</Badge>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => onRevoke(a.advisor_user_id)}>Revoke access</Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {rows === null && <p className="text-sm text-muted-foreground">Loading…</p>}
        {rows && rows.length === 0 && (
          <Card className="p-8 text-center text-sm text-muted-foreground">
            No scenarios yet. <Link to="/" className="underline">Create one</Link> — drafts auto-save once you sign in.
          </Card>
        )}
        {rows && rows.length > 0 && (
          <ul className="space-y-2">
            {rows.map((s) => (
              <li key={s.id}>
                <Card className="p-4 space-y-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{s.name || "Untitled draft"}</span>
                        <StatusBadge status={(s.status as ScenarioStatus) ?? (s.is_draft ? "draft" : "saved")} />
                        {s.loan_id && <Badge variant="outline" className="font-mono text-xs">{s.loan_id}</Badge>}
                        {(s as unknown as { archived_at?: string | null }).archived_at && (
                          <Badge variant="outline" className="text-xs">Archived</Badge>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        Updated {new Date(s.updated_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" onClick={() => nav(`/?scenario=${s.id}`)}>Open</Button>
                      <Button size="sm" variant="outline" onClick={() => onCopy(s)}>Copy</Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setOpenTracker(openTracker === s.id ? null : s.id)}
                      >
                        {openTracker === s.id ? "Hide tracking" : "Track payments"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          const isArchived = !!(s as unknown as { archived_at?: string | null }).archived_at;
                          const next = isArchived ? null : new Date().toISOString();
                          const { error } = await supabase
                            .from("scenarios")
                            .update({ archived_at: next })
                            .eq("id", s.id);
                          if (error) { toast.error(error.message); return; }
                          setRows((r) => r ? r.map((x) => x.id === s.id ? ({ ...x, archived_at: next } as ScenarioRow) : x) : r);
                          toast.success(isArchived ? "Unarchived" : "Archived");
                        }}
                      >
                        {(s as unknown as { archived_at?: string | null }).archived_at ? "Unarchive" : "Archive"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onDelete(s.id)}>Delete</Button>
                    </div>
                  </div>
                  {openTracker === s.id && user && (
                    <ActualPaymentTracking scenario={s} userId={user.id} />
                  )}
                </Card>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}